import { computed, ref } from '@vue/composition-api';
import { defineStore } from 'pinia';

import { useUiState } from '~/composables';
import { Logger } from '~/helpers/logger';
import { getPrice } from '~/modules/catalog/product/getters/productGetters';
import useCart from '~/modules/core/composables/useCart';
import { useCartStore } from '~/modules/core/stores/cart';
import type { BundleProduct, ProductInterface } from '~/modules/GraphQL/types';

import type { BundleOption, BundleProductsInterface, BundleProductsStoreInterface, IBundleProduct } from './types';

export const useBundleStore = defineStore('bundleProductsStore', (): BundleProductsStoreInterface => {
  const numberOfSets = ref(0);
  const { addItem, loading, error } = useCart();
  const cartStore = useCartStore();
  const { toggleBundleLayer } = useUiState();
  const separateBundleProducts = ref([]);
  const bundleProducts = ref<BundleProductsInterface>({
    cart_id: cartStore.cart.id || '',
    cart_items: [
      {
        data: {
          sku: null,
          quantity: 1
        },
        bundle_options: []
      }
    ]
  });

  const getBundleProductsQuantitySum = computed(() => {
    return getBundleOptions.value.reduce((memo: number, cur) => {
      memo = cur.quantity + memo;

      return memo;
    }, 0);
  });

  const isMaxProductsAdded = computed(() => {
    const isBundleFullyConfigured = getBundleOptions.value.length === numberOfSets.value;
    const isQuantitySumEqualSets = getBundleProductsQuantitySum.value === numberOfSets.value;

    return isBundleFullyConfigured || isQuantitySumEqualSets;
  });
  const getBundleOptions = computed(() => bundleProducts.value.cart_items[0].bundle_options);

  const setBundleSKU = (sku: string): void => {
    if (sku) {
      bundleProducts.value.cart_items[0].data.sku = sku;
    }
  };

  const getSeparateBundleProducts = () => {
    separateBundleProducts.value = getBundleOptions.value.flatMap((product) => {
      return Array.from({ length: product.quantity }, () => ({ id: product.id, quantity: 1, additional_info: product.additional_info }));
    });
  };

  const removeProductFromLayer = (index: number, id: string): void => {
    separateBundleProducts.value.splice(index - 1, 1);
    const currentBundle = getCurrentBundle(id);
    if (currentBundle.quantity === 1) {
      const index = getBundleOptions.value.findIndex((bundle) => bundle.id === id);
      getBundleOptions.value.splice(index, 1);
    } else {
      currentBundle.quantity -= 1;
    }
  };

  const getCurrentBundle = (id: string): BundleOption => {
    return getBundleOptions.value.find((bundle) => bundle.id === id);
  };

  const getProductQuantity = (product: ProductInterface): number => {
    return getCurrentBundle(product.uid)?.quantity || 0;
  };

  const getProductPrice = (product) => {
    return getPrice(product).regular;
  };

  const getDynamicPrice = computed(() => {
    return getBundleOptions.value.reduce((memo, cur) => {
      memo += cur?.additional_info?.price * cur.quantity;
      return memo;
    }, 0);
  });

  const addBundleProduct = (product: IBundleProduct, quantity: number): void => {
    const currentBundle = getCurrentBundle(product.uid);
    if (!currentBundle) {
      const newBundle = {
        id: product.uid,
        quantity: quantity,
        value: [product.uid],
        additional_info: {
          image: product?.product.thumbnail?.url,
          product_name: product?.product.name,
          price: getProductPrice(product.product)
        }
      };
      getBundleOptions.value.push(newBundle);
    } else if (currentBundle && quantity === 0) {
      const index = getBundleOptions.value.findIndex((product) => product.id === currentBundle.id);
      getBundleOptions.value.splice(index, 1);
    } else {
      currentBundle.quantity = quantity;
    }
    getSeparateBundleProducts();
  };

  const resetState = () => {
    separateBundleProducts.value = [];
    bundleProducts.value = {
      cart_id: cartStore.cart.id || '',
      cart_items: [
        {
          data: {
            sku: null,
            quantity: 1
          },
          bundle_options: []
        }
      ]
    };
  };

  const addBundleProductsToCart = async (product) => {
    if (product.price_type === 'DYNAMIC') {
      product.dynamic_price = getDynamicPrice.value;
    }
    try {
      await addItem({
        product: {
          ...product,
          bundle_options: getBundleOptions.value.map((product) => ({
            uid: product.id,
            value: product.quantity
          }))
        } as BundleProduct,
        quantity: 1
      });
      if (!error.value.addItem) {
        resetState();
        toggleBundleLayer();
      }
    } catch (e) {
      Logger.error('addBundleProductsToCart [ERROR]', e);
    }
  };
  return {
    numberOfSets,
    addBundleProduct,
    getProductQuantity,
    setBundleSKU,
    removeProductFromLayer,
    resetState,
    separateBundleProducts,
    getBundleProductsQuantitySum,
    bundleProducts,
    isMaxProductsAdded,
    getSeparateBundleProducts,
    addBundleProductsToCart,
    getDynamicPrice,
    loading: computed(() => loading.value)
  };
});
